﻿import { Image, View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LeagueGroup, RegionGroup, useAllFootballGames } from '@/hooks/useAllFootballGames';
import { useWorldCupGames } from '@/hooks/useWorldCupGames';
import { MatchCard } from '@/components/MatchCard';
import { EmptyState } from '@/components/EmptyState';
import { Colors } from '@/constants/colors';
import { FootballGame } from '@/types';
import { sameFootballMatch } from '@/utils/teamMatching';

type RouteName = '/futebol' | '/nba' | '/nfl' | '/copa' | '/favoritos' | '/alertas';
type HomeMode = 'copa' | 'futebol';

const LOGO = require('@/assets/logo-transparent.png');

function isLive(game: FootballGame): boolean {
  return game.status === 'live' || game.status === 'halftime';
}

function isScheduled(game: FootballGame): boolean {
  return game.status === 'scheduled';
}

function isFinished(game: FootballGame): boolean {
  return game.status === 'finished';
}

function gameTime(game: FootballGame): number {
  return new Date(game.startTime).getTime();
}

function dateKeyFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayKey(): string {
  return dateKeyFromDate(new Date());
}

function gameDayKey(game: FootballGame): string {
  try {
    const date = new Date(game.startTime);
    if (Number.isNaN(date.getTime())) return game.startTime.slice(0, 10);
    return dateKeyFromDate(date);
  } catch {
    return game.startTime.slice(0, 10);
  }
}

function normalizeText(value: string | undefined): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

function dateFromKey(dateKey: string): Date {
  return new Date(`${dateKey}T12:00:00`);
}

function addDaysToKey(dateKey: string, amount: number): string {
  const date = dateFromKey(dateKey);
  date.setDate(date.getDate() + amount);
  return dateKeyFromDate(date);
}

function formatSelectedDateTitle(dateKey: string): string {
  const date = dateFromKey(dateKey);
  const today = todayKey();
  const prefix = dateKey === today
    ? 'Hoje'
    : date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  const formatted = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
  return `${prefix}, ${formatted}`;
}

function buildDays(selectedDate: string) {
  const formatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });
  const center = dateFromKey(selectedDate);
  return [-2, -1, 0, 1, 2].map((offset) => {
    const date = new Date(center);
    date.setDate(center.getDate() + offset);
    const key = dateKeyFromDate(date);
    const isToday = key === todayKey();
    return {
      key,
      label: isToday ? 'HOJE' : formatter.format(date).replace('.', '').toUpperCase(),
      day: date.getDate().toString().padStart(2, '0'),
      active: key === selectedDate,
    };
  });
}

function SportChip({
  icon,
  label,
  color,
  active,
  onPress,
}: {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  label: string;
  color: string;
  active?: boolean;
  onPress: () => void;
}) {

  return (
    <TouchableOpacity
      style={[styles.sportChip, active && { borderColor: color, backgroundColor: `${color}18` }]}
      onPress={onPress}
      activeOpacity={0.75}
    >
      <MaterialCommunityIcons name={icon} size={18} color={color} />
      <Text style={[styles.sportChipText, active && { color }]}>{label}</Text>
    </TouchableOpacity>
  );
}

function SectionTitle({ title, action }: { title: string; action?: string }) {
  return (
    <View style={styles.sectionTitleRow}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {action ? <Text style={styles.sectionAction}>{action}</Text> : null}
    </View>
  );
}

type FeaturedLeague = {
  id: string;
  label: string;
  shortLabel: string;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  color: string;
  match: (game: FootballGame) => boolean;
};

const FEATURED_LEAGUES: FeaturedLeague[] = [
  {
    id: 'world-cup',
    label: 'Copa do Mundo',
    shortLabel: 'COPA',
    icon: 'trophy',
    color: '#facc15',
    match: (game) => {
      const league = normalizeText(game.league.name);
      return league.includes('world cup') || league.includes('copa do mundo');
    },
  },
  {
    id: 'brasileirao',
    label: 'Campeonato Brasileiro',
    shortLabel: 'BR',
    icon: 'flag',
    color: '#22c55e',
    match: (game) => {
      const league = normalizeText(game.league.name);
      const country = normalizeText(game.league.country);
      return league.includes('brasileirao') || league.includes('campeonato brasileiro') || (country.includes('brazil') && league.includes('serie a'));
    },
  },
  {
    id: 'premier-league',
    label: 'Premier League',
    shortLabel: 'ENG',
    icon: 'shield-star',
    color: '#ffffff',
    match: (game) => normalizeText(game.league.name).includes('premier league'),
  },
  {
    id: 'la-liga',
    label: 'La Liga',
    shortLabel: 'ESP',
    icon: 'soccer',
    color: '#f97316',
    match: (game) => {
      const league = normalizeText(game.league.name).replace(/\s+/g, '');
      return league.includes('laliga') || normalizeText(game.league.name).includes('la liga');
    },
  },
  {
    id: 'serie-a',
    label: 'SÃ©rie A TIM',
    shortLabel: 'ITA',
    icon: 'shield',
    color: '#38bdf8',
    match: (game) => {
      const league = normalizeText(game.league.name);
      const country = normalizeText(game.league.country);
      return country.includes('italy') && league.includes('serie a');
    },
  },
  {
    id: 'bundesliga',
    label: 'Bundesliga',
    shortLabel: 'ALE',
    icon: 'soccer-field',
    color: '#ef4444',
    match: (game) => normalizeText(game.league.name).includes('bundesliga'),
  },
  {
    id: 'ligue-1',
    label: 'Ligue 1 FranÃ§a',
    shortLabel: 'FRA',
    icon: 'hexagon-multiple',
    color: '#60a5fa',
    match: (game) => {
      const league = normalizeText(game.league.name);
      const country = normalizeText(game.league.country);
      return league.includes('ligue 1') || (country.includes('france') && (league.includes('league one') || league.includes('ligue')));
    },
  },
];

type FeaturedGroup = FeaturedLeague & {
  games: FootballGame[];
  liveCount: number;
  finishedCount: number;
};

function sortGamesForGroup(games: FootballGame[]): FootballGame[] {
  return [...games].sort((a, b) => {
    const rank = (game: FootballGame) => isLive(game) ? 0 : isScheduled(game) ? 1 : isFinished(game) ? 2 : 3;
    return rank(a) - rank(b) || gameTime(a) - gameTime(b);
  });
}

function addUniqueGame(list: FootballGame[], game: FootballGame) {
  const exists = list.some((item) => item.id === game.id || sameFootballMatch(item, game));
  if (!exists) list.push(game);
}

function buildFeaturedGroups(games: FootballGame[], worldCupGames: FootballGame[]): FeaturedGroup[] {
  const today = todayKey();
  const todayGames = games.filter((game) => gameDayKey(game) === today || isLive(game));
  const todaysWorldCup = worldCupGames.filter((game) => gameDayKey(game) === today || isLive(game));

  return FEATURED_LEAGUES.map((league) => {
    const groupGames: FootballGame[] = [];
    todayGames.filter(league.match).forEach((game) => addUniqueGame(groupGames, game));
    if (league.id === 'world-cup') {
      todaysWorldCup.forEach((game) => addUniqueGame(groupGames, game));
    }

    const sortedGames = sortGamesForGroup(groupGames);
    return {
      ...league,
      games: sortedGames,
      liveCount: sortedGames.filter(isLive).length,
      finishedCount: sortedGames.filter(isFinished).length,
    };
  });
}

function formatWorldCupDate(date: string, label: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  const formatted = parsed.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).replace('.', '');
  return label === 'Hoje' ? `Hoje - ${formatted}` : formatted;
}

function buildWorldCupDays(days: { date: string; label: string; games: FootballGame[] }[]) {
  const today = todayKey();

  const dayRank = (day: { date: string; games: FootballGame[] }) => {
    if (day.games.some(isLive)) return 0;
    if (day.date === today) return 1;
    if (day.date > today) return 2;
    return 3;
  };

  const compareDays = (
    a: { date: string; games: FootballGame[] },
    b: { date: string; games: FootballGame[] }
  ) => {
    const rankDiff = dayRank(a) - dayRank(b);
    if (rankDiff !== 0) return rankDiff;
    return dayRank(a) === 3 ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
  };

  const compareGames = (a: FootballGame, b: FootballGame) => {
    const liveDiff = Number(!isLive(a)) - Number(!isLive(b));
    return liveDiff || gameTime(a) - gameTime(b);
  };

  return days
    .filter((day) => day.games.length > 0)
    .sort(compareDays)
    .map((day) => ({
      ...day,
      title: formatWorldCupDate(day.date, day.label),
      games: [...day.games].sort(compareGames),
      liveCount: day.games.filter(isLive).length,
    }));
}

function LeagueAccordion({
  group,
  expanded,
  onToggle,
}: {
  group: FeaturedGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const countLabel = group.games.length === 1 ? '1 jogo' : `${group.games.length} jogos`;

  return (
    <View style={[styles.accordion, expanded && { borderColor: group.color }]}>
      <TouchableOpacity style={styles.accordionHeader} onPress={onToggle} activeOpacity={0.76}>
        <View style={[styles.leagueIconShell, { backgroundColor: `${group.color}20` }]}>
          <MaterialCommunityIcons name={group.icon} size={22} color={group.color} />
        </View>
        <View style={styles.accordionTitleBlock}>
          <Text style={styles.accordionTitle} numberOfLines={1}>{group.label}</Text>
          <Text style={styles.accordionMeta}>
            {countLabel}{group.liveCount > 0 ? ` Â· ${group.liveCount} ao vivo` : ''}{group.finishedCount > 0 ? ` Â· ${group.finishedCount} encerrado${group.finishedCount > 1 ? 's' : ''}` : ''}
          </Text>
        </View>
        <View style={styles.leagueTag}>
          <Text style={styles.leagueTagText}>{group.shortLabel}</Text>
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={Colors.textSecondary} />
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.accordionBody}>
          {group.games.length > 0 ? (
            group.games.map((game) => (
              <MatchCard key={`${group.id}-${game.sport}-${game.id}`} game={game} compact />
            ))
          ) : (
            <View style={styles.groupEmpty}>
              <Text style={styles.groupEmptyTitle}>Sem jogos hoje</Text>
              <Text style={styles.groupEmptyText}>Quando houver partida deste campeonato, ela aparece aqui.</Text>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}

function WorldCupDaySection({
  day,
  expanded,
  onToggle,
}: {
  day: { date: string; title: string; games: FootballGame[]; liveCount: number };
  expanded: boolean;
  onToggle: () => void;
}) {
  const liveGames = day.games.filter(isLive);
  const scheduledGames = day.games.filter(isScheduled);
  const finishedGames = day.games.filter(isFinished);
  const otherGames = day.games.filter((game) => !isLive(game) && !isScheduled(game) && !isFinished(game));
  const meta = [
    `${day.games.length} ${day.games.length === 1 ? 'jogo' : 'jogos'}`,
    liveGames.length > 0 ? `${liveGames.length} ao vivo` : null,
    scheduledGames.length > 0 ? `${scheduledGames.length} próximos` : null,
    finishedGames.length > 0 ? `${finishedGames.length} encerrados` : null,
  ].filter(Boolean).join(' · ');
  const sections = [
    { id: 'live', title: 'Ao vivo', color: Colors.futebol, games: liveGames },
    { id: 'scheduled', title: 'Próximos jogos', color: '#60a5fa', games: scheduledGames },
    { id: 'finished', title: 'Encerrados', color: Colors.textMuted, games: finishedGames },
    { id: 'other', title: 'Outros jogos', color: Colors.textSecondary, games: otherGames },
  ].filter((section) => section.games.length > 0);

  return (
    <View style={styles.worldCupDaySection}>
      <TouchableOpacity style={styles.worldCupDayHeader} onPress={onToggle} activeOpacity={0.78}>
        <View>
          <Text style={styles.worldCupDayTitle}>{day.title}</Text>
          <Text style={styles.worldCupDayMeta}>{meta}</Text>
        </View>
        <View style={styles.worldCupDayHeaderIcon}>
          <MaterialCommunityIcons name="calendar-clock" size={20} color="#facc15" />
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={Colors.textSecondary} />
        </View>
      </TouchableOpacity>
      {expanded ? (
        <View style={styles.worldCupGamesList}>
          {sections.length > 0 ? (
            sections.map((section) => (
              <View key={`${day.date}-${section.id}`} style={styles.worldCupStatusGroup}>
                <View style={styles.worldCupStatusHeader}>
                  <View style={[styles.worldCupStatusDot, { backgroundColor: section.color }]} />
                  <Text style={styles.worldCupStatusTitle}>{section.title}</Text>
                  <Text style={styles.worldCupStatusCount}>{section.games.length}</Text>
                </View>
                {section.games.map((game) => (
                  <MatchCard key={`world-cup-${day.date}-${section.id}-${game.sport}-${game.id}`} game={game} compact />
                ))}
              </View>
            ))
          ) : (
            <View style={styles.groupEmpty}>
              <Text style={styles.groupEmptyTitle}>Sem jogos da Copa neste dia</Text>
              <Text style={styles.groupEmptyText}>Escolha outro dia para ver a agenda da Copa do Mundo.</Text>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}

function leagueMeta(league: LeagueGroup): string {
  const liveCount = league.games.filter(isLive).length;
  const finishedCount = league.games.filter(isFinished).length;
  return [
    `${league.games.length} ${league.games.length === 1 ? 'jogo' : 'jogos'}`,
    liveCount > 0 ? `${liveCount} ao vivo` : null,
    finishedCount > 0 ? `${finishedCount} encerrado${finishedCount > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ');
}

function RegionAccordion({
  region,
  expanded,
  onToggle,
}: {
  region: RegionGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = [
    `${region.gamesCount} ${region.gamesCount === 1 ? 'jogo' : 'jogos'}`,
    region.liveCount > 0 ? `${region.liveCount} ao vivo` : null,
  ].filter(Boolean).join(' · ');

  return (
    <View style={[styles.regionAccordion, expanded && styles.regionAccordionOpen]}>
      <TouchableOpacity style={styles.regionHeader} onPress={onToggle} activeOpacity={0.76}>
        <View style={styles.regionIconShell}>
          <MaterialCommunityIcons name="map-marker-radius" size={20} color={Colors.futebol} />
        </View>
        <View style={styles.regionTitleBlock}>
          <Text style={styles.regionTitle} numberOfLines={1}>{region.name}</Text>
          <Text style={styles.regionMeta}>{meta}</Text>
        </View>
        <View style={styles.regionTag}>
          <Text style={styles.regionTagText}>{region.tag}</Text>
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={Colors.textSecondary} />
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.regionBody}>
          {region.leagueGroups.map((league) => (
            <View key={`${region.id}-${league.id}`} style={styles.regionLeagueBlock}>
              <View style={styles.regionLeagueHeader}>
                {league.logo ? <Image source={{ uri: league.logo }} style={styles.regionLeagueLogo} resizeMode="contain" /> : null}
                <View style={styles.regionLeagueTitleBlock}>
                  <Text style={styles.regionLeagueTitle} numberOfLines={1}>{league.name}</Text>
                  <Text style={styles.regionLeagueMeta}>{leagueMeta(league)}</Text>
                </View>
              </View>
              {league.games.map((game) => (
                <MatchCard key={`${region.id}-${league.id}-${game.sport}-${game.id}`} game={game} compact />
              ))}
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

export default function FutebolScreen() {
  const router = useRouter();
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const football = useAllFootballGames(true, selectedDate);
  const worldCup = useWorldCupGames();
  const [homeMode, setHomeMode] = useState<HomeMode>('copa');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({ 'world-cup': true });
  const [expandedWorldCupDays, setExpandedWorldCupDays] = useState<Record<string, boolean>>({});
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});
  const days = useMemo(() => buildDays(selectedDate), [selectedDate]);

  const allGames = useMemo<FootballGame[]>(() => {
    const byKey = new Map<string, FootballGame>();
    football.games.forEach((game) => byKey.set(game.id, game));
    return Array.from(byKey.values()).sort((a, b) => {
      const statusOrder = Number(!isLive(a)) - Number(!isLive(b));
      return statusOrder || gameTime(a) - gameTime(b);
    });
  }, [football.games]);

  const featuredGroups = useMemo(
    () => buildFeaturedGroups(allGames, worldCup.games),
    [allGames, worldCup.games]
  );
  const selectedWorldCupGames = useMemo(() => {
    const day = worldCup.days.find((item) => item.date === selectedDate);
    return sortGamesForGroup(day?.games ?? []);
  }, [selectedDate, worldCup.days]);
  const selectedWorldCupDay = useMemo(() => ({
    date: selectedDate,
    title: formatWorldCupDate(selectedDate, selectedDate === todayKey() ? 'Hoje' : ''),
    games: selectedWorldCupGames,
    liveCount: selectedWorldCupGames.filter(isLive).length,
  }), [selectedDate, selectedWorldCupGames]);
  const otherRegionGroups = useMemo(
    () => football.regionGroups.filter((region) => region.id !== 'world-cup'),
    [football.regionGroups]
  );
  const visibleGamesCount = selectedWorldCupGames.length + otherRegionGroups.reduce((sum, region) => sum + region.gamesCount, 0);
  const liveGames = allGames.filter(isLive);
  const loading = football.loading || worldCup.loading;
  const selectedDateTitle = formatSelectedDateTitle(selectedDate);

  function refresh() {
    football.refresh();
    worldCup.refresh();
  }

  function goTo(route: RouteName) {
    router.push(route);
  }

  function isGroupExpanded(group: FeaturedGroup): boolean {
    return expandedGroups[group.id] ?? (group.id === 'world-cup' || group.liveCount > 0);
  }

  function toggleGroup(group: FeaturedGroup) {
    const expanded = isGroupExpanded(group);
    setExpandedGroups((state) => ({ ...state, [group.id]: !expanded }));
  }

  function isWorldCupDayExpanded(day: { date: string; liveCount: number }): boolean {
    return expandedWorldCupDays[day.date] ?? (day.liveCount > 0 || day.date === todayKey());
  }

  function toggleWorldCupDay(day: { date: string; liveCount: number }) {
    const expanded = isWorldCupDayExpanded(day);
    setExpandedWorldCupDays((state) => ({ ...state, [day.date]: !expanded }));
  }

  function isRegionExpanded(region: RegionGroup): boolean {
    return expandedRegions[region.id] ?? (region.liveCount > 0 || region.priority <= 4);
  }

  function toggleRegion(region: RegionGroup) {
    const expanded = isRegionExpanded(region);
    setExpandedRegions((state) => ({ ...state, [region.id]: !expanded }));
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={Colors.futebol} />}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Image source={LOGO} style={styles.logo} resizeMode="contain" />
          <TouchableOpacity style={styles.bellButton} onPress={() => goTo('/alertas')} activeOpacity={0.75}>
            <Ionicons name="notifications-outline" size={22} color={Colors.text} />
            {liveGames.length > 0 && <View style={styles.bellDot} />}
          </TouchableOpacity>
        </View>

        <View style={styles.greetingBlock}>
          <Text style={styles.greeting}>{getGreeting()}, <Text style={styles.nameAccent}>HP Sports!</Text></Text>
          <Text style={styles.subtitle}>Acompanhe seus esportes favoritos</Text>
        </View>

        <TouchableOpacity style={styles.searchBox} activeOpacity={0.75}>
          <Ionicons name="search" size={20} color={Colors.textMuted} />
          <Text style={styles.searchText}>Pesquisar time, liga ou pais</Text>
        </TouchableOpacity>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sportsRow}>
          <SportChip icon="trophy" label="Copa 2026" color="#facc15" active={homeMode === 'copa'} onPress={() => setHomeMode('copa')} />
          <SportChip icon="soccer" label="Futebol" color={Colors.futebol} active={homeMode === 'futebol'} onPress={() => setHomeMode('futebol')} />
          <SportChip icon="basketball" label="NBA" color={Colors.nba} onPress={() => goTo('/nba')} />
          <SportChip icon="football" label="NFL" color={Colors.nfl} onPress={() => goTo('/nfl')} />
        </ScrollView>

        <View style={styles.dateHeader}>
          <Text style={styles.todayText}>{selectedDateTitle}</Text>
          <TouchableOpacity style={styles.calendarButton} onPress={() => goTo('/copa')} activeOpacity={0.75}>
            <Text style={styles.calendarText}>Ver calendario</Text>
            <Ionicons name="calendar-outline" size={15} color={Colors.futebol} />
          </TouchableOpacity>
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dateRow}>
          {days.map((day) => (
            <TouchableOpacity
              key={day.key}
              style={[styles.dateCard, day.active && styles.dateCardActive]}
              onPress={() => setSelectedDate(day.key)}
              activeOpacity={0.78}
            >
              <Text style={[styles.dateWeek, day.active && styles.dateActiveText]}>{day.label}</Text>
              <Text style={[styles.dateDay, day.active && styles.dateActiveText]}>{day.day}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={styles.nextDateButton}
            onPress={() => setSelectedDate((current) => addDaysToKey(current, 1))}
            activeOpacity={0.75}
          >
            <Ionicons name="chevron-forward" size={20} color={Colors.textSecondary} />
          </TouchableOpacity>
        </ScrollView>

        <SectionTitle
          title="Agenda do dia"
          action={visibleGamesCount > 0 ? `${visibleGamesCount} jogos` : undefined}
        />
        <View style={styles.worldCupDaysList}>
          <WorldCupDaySection
            day={selectedWorldCupDay}
            expanded={isWorldCupDayExpanded(selectedWorldCupDay)}
            onToggle={() => toggleWorldCupDay(selectedWorldCupDay)}
          />
        </View>

        <SectionTitle
          title="Outros campeonatos por regiao"
          action={otherRegionGroups.length > 0 ? `${otherRegionGroups.length} regioes` : undefined}
        />
        {otherRegionGroups.length > 0 ? (
          <View style={styles.regionList}>
            {otherRegionGroups.map((region) => (
              <RegionAccordion
                key={region.id}
                region={region}
                expanded={isRegionExpanded(region)}
                onToggle={() => toggleRegion(region)}
              />
            ))}
          </View>
        ) : !loading ? (
          <View style={styles.emptyPanel}>
            <EmptyState
              icon="CAL"
              title="Sem outros jogos neste dia"
              subtitle="Escolha outro dia ou puxe para atualizar a agenda."
            />
          </View>
        ) : null}

        {football.error ? <Text style={styles.warningText}>Aviso: {football.error}</Text> : null}
        {worldCup.error ? <Text style={styles.warningText}>Aviso Copa: {worldCup.error}</Text> : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },
  content: {
    paddingBottom: 104,
  },
  header: {
    height: 58,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    width: 88,
    height: 42,
  },
  bellButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  bellDot: {
    position: 'absolute',
    right: 9,
    top: 9,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.futebol,
  },
  greetingBlock: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  greeting: {
    color: Colors.text,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: '900',
  },
  nameAccent: {
    color: Colors.futebol,
  },
  subtitle: {
    color: Colors.textSecondary,
    fontSize: 14,
    marginTop: 3,
  },
  searchBox: {
    marginHorizontal: 20,
    marginTop: 18,
    height: 48,
    borderRadius: 16,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  searchText: {
    color: Colors.textMuted,
    fontSize: 14,
    fontWeight: '600',
  },
  sportsRow: {
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  sportChip: {
    width: 132,
    minHeight: 44,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: Colors.surface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  sportChipText: {
    color: Colors.textSecondary,
    fontSize: 12,
    fontWeight: '900',
  },
  dateHeader: {
    paddingHorizontal: 20,
    paddingTop: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  todayText: {
    color: Colors.text,
    fontSize: 15,
    fontWeight: '900',
    textTransform: 'capitalize',
  },
  calendarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  calendarText: {
    color: Colors.futebol,
    fontSize: 12,
    fontWeight: '900',
  },
  dateRow: {
    paddingHorizontal: 20,
    paddingTop: 14,
    gap: 11,
  },
  dateCard: {
    width: 62,
    height: 58,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  dateCardActive: {
    backgroundColor: Colors.futebol,
    borderColor: Colors.futebol,
  },
  dateWeek: {
    color: Colors.textMuted,
    fontSize: 11,
    fontWeight: '900',
  },
  dateDay: {
    color: Colors.text,
    fontSize: 18,
    fontWeight: '900',
    marginTop: 3,
  },
  dateActiveText: {
    color: Colors.black,
  },
  nextDateButton: {
    width: 42,
    height: 58,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  sectionTitleRow: {
    paddingHorizontal: 20,
    paddingTop: 26,
    paddingBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: Colors.text,
    fontSize: 14,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  sectionAction: {
    color: Colors.futebol,
    fontSize: 12,
    fontWeight: '900',
  },
  emptyPanel: {
    marginHorizontal: 16,
    backgroundColor: Colors.card,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
  },
  worldCupDaysList: {
    paddingHorizontal: 16,
    gap: 12,
  },
  worldCupDaySection: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    backgroundColor: Colors.card,
    overflow: 'hidden',
  },
  worldCupDayHeader: {
    minHeight: 58,
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  worldCupDayHeaderIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  worldCupDayTitle: {
    color: Colors.text,
    fontSize: 14,
    fontWeight: '900',
    textTransform: 'capitalize',
  },
  worldCupDayMeta: {
    color: Colors.textSecondary,
    fontSize: 11,
    fontWeight: '700',
    marginTop: 3,
  },
  worldCupGamesList: {
    paddingBottom: 8,
  },
  worldCupStatusGroup: {
    paddingTop: 10,
  },
  worldCupStatusHeader: {
    minHeight: 28,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  worldCupStatusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  worldCupStatusTitle: {
    flex: 1,
    color: Colors.text,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  worldCupStatusCount: {
    minWidth: 20,
    textAlign: 'right',
    color: Colors.textSecondary,
    fontSize: 11,
    fontWeight: '900',
  },
  regionList: {
    paddingHorizontal: 16,
    gap: 10,
  },
  regionAccordion: {
    backgroundColor: Colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    overflow: 'hidden',
  },
  regionAccordionOpen: {
    borderColor: Colors.futebol,
  },
  regionHeader: {
    minHeight: 64,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  regionIconShell: {
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(82, 255, 39, 0.12)',
  },
  regionTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  regionTitle: {
    color: Colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  regionMeta: {
    color: Colors.textMuted,
    fontSize: 11,
    fontWeight: '700',
    marginTop: 3,
  },
  regionTag: {
    minWidth: 42,
    height: 24,
    paddingHorizontal: 7,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  regionTagText: {
    color: Colors.textSecondary,
    fontSize: 10,
    fontWeight: '900',
  },
  regionBody: {
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  regionLeagueBlock: {
    paddingTop: 10,
  },
  regionLeagueHeader: {
    minHeight: 34,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  regionLeagueLogo: {
    width: 22,
    height: 22,
  },
  regionLeagueTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  regionLeagueTitle: {
    color: Colors.text,
    fontSize: 12,
    fontWeight: '900',
  },
  regionLeagueMeta: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
  },
  accordionList: {
    paddingHorizontal: 16,
    gap: 10,
  },
  accordion: {
    backgroundColor: Colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    overflow: 'hidden',
  },
  accordionHeader: {
    minHeight: 66,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  leagueIconShell: {
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  accordionTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  accordionTitle: {
    color: Colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  accordionMeta: {
    color: Colors.textMuted,
    fontSize: 11,
    fontWeight: '700',
    marginTop: 3,
  },
  leagueTag: {
    minWidth: 38,
    height: 24,
    paddingHorizontal: 7,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  leagueTagText: {
    color: Colors.textSecondary,
    fontSize: 10,
    fontWeight: '900',
  },
  accordionBody: {
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  groupEmpty: {
    marginHorizontal: 12,
    marginTop: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  groupEmptyTitle: {
    color: Colors.text,
    fontSize: 12,
    fontWeight: '900',
  },
  groupEmptyText: {
    color: Colors.textSecondary,
    fontSize: 11,
    lineHeight: 16,
    marginTop: 3,
  },
  leagueRow: {
    paddingHorizontal: 20,
    gap: 10,
  },
  leagueTile: {
    width: 88,
    minHeight: 92,
    borderRadius: 12,
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
  },
  leagueTileText: {
    color: Colors.text,
    fontSize: 10,
    lineHeight: 13,
    textAlign: 'center',
    fontWeight: '800',
  },
  warningText: {
    color: '#fca5a5',
    marginHorizontal: 20,
    marginTop: 18,
    fontSize: 12,
    lineHeight: 17,
  },
});
